import { z } from 'zod';
import { ATTACHMENT_MAX_FILE_SIZE_BYTES } from '@/constants/uploadLimits';

// Image File Schema

export interface ImageFileSchemaOptions {
  maxFileSize?: number;
  minDimensions?: { width: number; height: number };
  maxDimensions?: { width: number; height: number };
  acceptedImageTypes?: string[];
  required?: boolean;
}

const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};
export const createImageFileSchema = (options: ImageFileSchemaOptions = {}) => {
  const {
    maxFileSize = ATTACHMENT_MAX_FILE_SIZE_BYTES, // 100 MB
    minDimensions = { width: 50, height: 50 },
    maxDimensions = { width: 4096, height: 4096 },
    acceptedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
    required = true,
  } = options;

  const imageSchema = z
    .union([
      z.instanceof(File, {
        message: 'Please select an image file.',
      }),
      z.null(),
      z.undefined(),
    ])
    .refine(
      (file) => {
        // If no file is provided, it's valid (optional field)
        if (!file) return true;

        // Validate file size
        return file.size <= maxFileSize;
      },
      {
        message: `The image is too large. Please choose an image smaller than ${formatBytes(maxFileSize)}.`,
      },
    )
    .refine(
      (file) => {
        // If no file is provided, it's valid (optional field)
        if (!file) return true;

        // Validate file type
        return acceptedImageTypes.includes(file.type);
      },
      {
        message: 'Please upload a valid image file (JPEG, PNG, or WebP).',
      },
    )
    .refine(
      (file) => {
        // If no file is provided, it's valid (optional field)
        if (!file) return true;

        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
              const meetsDimensions =
                img.width >= minDimensions.width &&
                img.height >= minDimensions.height &&
                img.width <= maxDimensions.width &&
                img.height <= maxDimensions.height;
              resolve(meetsDimensions);
            };
            img.src = e.target?.result as string;
          };
          reader.readAsDataURL(file);
        });
      },
      {
        message: `The image dimensions are invalid. Please upload an image between ${minDimensions.width}x${minDimensions.height} and ${maxDimensions.width}x${maxDimensions.height} pixels.`,
      },
    );

  if (!required) {
    return imageSchema.optional();
  }

  return imageSchema;
};

export const createImageFileHelperText = (
  options: ImageFileSchemaOptions = {},
) => {
  const {
    maxFileSize = ATTACHMENT_MAX_FILE_SIZE_BYTES, // 100 MB
    minDimensions = { width: 50, height: 50 },
    maxDimensions = { width: 4096, height: 4096 },
    acceptedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
    required = true,
  } = options;

  let helperText = `Please select images with maximum file size of ${formatBytes(
    maxFileSize,
  )}. Accepted file types: ${acceptedImageTypes.join(', ')}. Dimensions Range: ${minDimensions.width}x${minDimensions.height} - ${maxDimensions.width}x${maxDimensions.height}  ${
    required ? '' : '(Optional)'
  }`;

  return helperText;
};

// Image File Array Schema

export interface ImageFileArraySchemaOptions {
  minFiles?: number;
  maxFiles?: number;
  maxFileSize?: number;
  minDimensions?: { width: number; height: number };
  maxDimensions?: { width: number; height: number };
  acceptedImageTypes?: string[];
  required?: boolean;
}
export const createImageFileArraySchema = (
  options: ImageFileArraySchemaOptions = {},
) => {
  const {
    minFiles = 0,
    maxFiles = 5,
    maxFileSize = ATTACHMENT_MAX_FILE_SIZE_BYTES, // 100 MB
    minDimensions = { width: 50, height: 50 },
    maxDimensions = { width: 4096, height: 4096 },
    acceptedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
    required = true,
  } = options;

  // Create a custom validation for the array that provides specific error messages
  let imageArraySchema = z.array(z.any()).superRefine(async (files, ctx) => {
    if (!files || files.length === 0) {
      return; // Empty array is valid for optional fields
    }

    const errors: string[] = [];

    // Apply array-level validations first
    if (required && minFiles && minFiles > 0) {
      if (!files || files.length < minFiles) {
        errors.push(`A minimum of ${minFiles} images must be uploaded`);
      }
    }

    if (maxFiles && maxFiles > 0) {
      if (files && files.length > maxFiles) {
        errors.push(`A maximum of ${maxFiles} images can be uploaded`);
      }
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Skip if no file
      if (!file) continue;

      // Validate file type
      if (!acceptedImageTypes.includes(file.type)) {
        errors.push(
          `Image ${i + 1} (${file.name}): Invalid file type. Please upload JPEG, PNG, or WebP.`,
        );
        continue;
      }

      // Validate file size
      if (file.size > maxFileSize) {
        errors.push(
          `Image ${i + 1} (${file.name}): File too large. Maximum size is ${formatBytes(maxFileSize)}.`,
        );
        continue;
      }

      // Validate dimensions
      try {
        const isValidDimensions = await new Promise<boolean>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
              const meetsDimensions =
                img.width >= minDimensions.width &&
                img.height >= minDimensions.height &&
                img.width <= maxDimensions.width &&
                img.height <= maxDimensions.height;
              resolve(meetsDimensions);
            };
            img.onerror = () => resolve(false);
            img.src = e.target?.result as string;
          };
          reader.onerror = () => resolve(false);
          reader.readAsDataURL(file);
        });

        if (!isValidDimensions) {
          errors.push(
            `Image ${i + 1} (${file.name}): Invalid dimensions. Must be between ${minDimensions.width}x${minDimensions.height} and ${maxDimensions.width}x${maxDimensions.height} pixels.`,
          );
        }
      } catch (error) {
        errors.push(
          `Image ${i + 1} (${file.name}): Could not validate image dimensions.`,
        );
      }
    }

    if (errors.length > 0) {
      // Add a single issue with all the error messages combined
      ctx.addIssue({
        code: 'custom',
        message: errors.join('\n'),
      });
    }
  });

  if (!required) {
    return imageArraySchema.optional();
  }

  return imageArraySchema;
};

export const createImageFileArrayHelperText = (
  options: ImageFileArraySchemaOptions = {},
) => {
  const {
    maxFiles = 5,
    maxFileSize = ATTACHMENT_MAX_FILE_SIZE_BYTES, // 100 MB
    minDimensions = { width: 50, height: 50 },
    maxDimensions = { width: 4096, height: 4096 },
    acceptedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
    required = true,
  } = options;

  let helperText = `Please select up to ${maxFiles} images with maximum file size of ${formatBytes(maxFileSize)}. Accepted file types: ${acceptedImageTypes.join(', ')}. Dimensions Range: ${minDimensions.width}x${minDimensions.height} - ${maxDimensions.width}x${maxDimensions.height}  ${
    required ? '' : '(Optional)'
  }`;

  return helperText;
};

export const imageUrlSchemaHelper = 'Please enter a valid image URL.';
export const imageUrlSchemaHelperOptional = `${imageUrlSchemaHelper}, optional`;
export const imageUrlSchema = z.string().url({ message: imageUrlSchemaHelper });
